import { useCallback } from "react";

import { useMediaQuery } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { screenResize } from "actions";

import { ScreenType } from "constants/screen";

import { getScreenType } from "selectors";

import { useTheme } from "./useTheme";

interface UseScreenSizeOptions {
  min?: number;
  max?: number;
}

interface UseSetScreenSizeReturn {
  setMobile: () => void;
  setDesktop: () => void;
}

function getBreakpointCss(
  minBreakpointIndex: number | undefined,
  maxBreakpointIndex: number | undefined,
  breakpoints: number[],
): string {
  if (
    typeof maxBreakpointIndex === "number" &&
    typeof minBreakpointIndex === "number"
  ) {
    return `(min-width: ${breakpoints[minBreakpointIndex]}px) and (max-width: ${breakpoints[maxBreakpointIndex]}px)`;
  } else if (typeof minBreakpointIndex === "number") {
    return `(min-width: ${breakpoints[minBreakpointIndex]}px)`;
  } else if (typeof maxBreakpointIndex === "number") {
    return `(max-width: ${breakpoints[maxBreakpointIndex]}px)`;
  }
  return "";
}

/**
 * Returns whether or not the device's current viewport matches the dimensions in the active theme's breakpoints list
 */
export function useScreenSize({ min, max }: UseScreenSizeOptions): boolean {
  const { breakpoints } = useTheme();
  return useMediaQuery(getBreakpointCss(min, max, breakpoints), {
    noSsr: true,
  });
}

/**
 * Returns whether or not the device's current viewport matches the dimensions of a mobile screen
 */
export function useIsMobile(): boolean {
  return !useScreenSize({ min: 2 });
}

/**
 * Returns whether or not the device's current viewport matches the dimensions of a small mobile screen
 */
export function useIsSmallMobile(): boolean {
  return !useScreenSize({ min: 1 });
}

/**
 * Returns whether or not the device's current viewport matches the dimensions of a very small mobile screen
 */
export function useIsExtraSmallMobile(): boolean {
  return !useScreenSize({ min: 0 });
}

/**
 * Returns whether or not the device's current viewport matches the dimensions of a desktop screen
 */
export function useIsDesktop(): boolean {
  return useScreenSize({ min: 2 });
}

/**
 * Returns whether the user has chosen the desktop view via the
 * "Mobile View" / "Desktop View" actions. Reflects the Redux
 * screen-type setting, not the raw viewport.
 */
export function useHasSelectedDesktopView(): boolean {
  return useSelector(getScreenType) === ScreenType.DESKTOP;
}

/**
 * Returns whether the user has chosen the mobile view via the
 * "Mobile View" / "Desktop View" actions. Reflects the Redux
 * screen-type setting, not the raw viewport.
 */
export function useHasSelectedMobileView(): boolean {
  return useSelector(getScreenType) !== ScreenType.DESKTOP;
}

/**
 * Returns callbacks for forcing the app's screen size to mobile or desktop
 * via `screenResize`. Used by "Mobile View" / "Desktop View" actions.
 */
export function useSetScreenSize(): UseSetScreenSizeReturn {
  const dispatch = useDispatch();
  const theme = useTheme();

  const setMobile = useCallback(
    () => dispatch(screenResize(theme.breakpoints[2] - 1)),
    [dispatch, theme],
  );
  const setDesktop = useCallback(
    () => dispatch(screenResize(theme.breakpoints[2])),
    [dispatch, theme],
  );

  return { setMobile, setDesktop };
}
